import {
  useContext,
  useState,
  useRef,
  useEffect,
  useLayoutEffect,
} from "react";
import { FaCaretDown } from "react-icons/fa";
import ReactTooltip from "react-tooltip";
import AppContext from "../../context";
import {
  cmToIn,
  decimalInchesToNearest16thFraction,
  inchFractionsToCmDecimal,
} from "../../util/helpers";
import { IMPERIAL_ACTIONS } from "../../util/hooks/dimensions";
import { readableColor } from "color2k";
import variables from "../../styles/export.module.scss";

const Dimensions = ({
  imperial,
  widthError,
  depthError,
  heightError,
  handleSetImperial,
  handleSetWidthError,
  handleSetDepthError,
  handleSetHeightError,
}) => {
  const [mounted, setMounted] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);
  const [width, setWidth] = useState(50.8);
  const [depth, setDepth] = useState(40.64);
  const [height, setHeight] = useState(6.35);
  const [inputsDisabled, setInputsDisabled] = useState(true);
  const [dimensionsErrorMessage, setDimensionsErrorMessage] = useState(
    "The dimensions are not allowed for this material."
  );
  const widthFractionDropdownRef = useRef(null);
  const depthFractionDropdownRef = useRef(null);
  const heightFractionDropdownRef = useRef(null);
  const [widthFractionsDropdownActive, setWidthFractionsDropdownActive] =
    useState(false);
  const [depthFractionsDropdownActive, setDepthFractionsDropdownActive] =
    useState(false);
  const [heightFractionsDropdownActive, setHeightFractionsDropdownActive] =
    useState(false);
  const [unitSwitchDisabled, setUnitSwitchDisabled] = useState(true);
  const {
    triggerPriceUpdate,
    triggerDimensionsUpdate,
    dimensionsUpdate,
    handleSetCurrentInsertDimensions,
    currentInsertDimensions,
    selectedMaterial,
    showTooltips,
    MAX_HEIGHT_DEFAULT,
    MAX_HEIGHT_BAMBOO,
    /*     MAX_HEIGHT_CEDAR, */
    MIN_HEIGHT,
    MIN_WIDTH_DEPTH,
    MAX_WIDTH_DEPTH,
    dimensionsState,
    dispatchDimensions: dispatch,
  } = useContext(AppContext);

  const FRACTIONS_V2 = [
    { numerator: 0, denominator: 1 },
    { numerator: 1, denominator: 16 },
    { numerator: 1, denominator: 8 },
    { numerator: 3, denominator: 16 },
    { numerator: 1, denominator: 4 },
    { numerator: 5, denominator: 16 },
    { numerator: 3, denominator: 8 },
    { numerator: 7, denominator: 16 },
    { numerator: 1, denominator: 2 },
    { numerator: 9, denominator: 16 },
    { numerator: 5, denominator: 8 },
    { numerator: 11, denominator: 16 },
    { numerator: 3, denominator: 4 },
    { numerator: 13, denominator: 16 },
    { numerator: 7, denominator: 8 },
    { numerator: 15, denominator: 16 },
  ];

  useLayoutEffect(() => {
    let widthInput = document.getElementById("outside_width");
    let depthInput = document.getElementById("outside_depth");
    let heightInput = document.getElementById("outside_height");
    let widthValue = isNaN(widthInput.valueAsNumber)
      ? width
      : widthInput.valueAsNumber;
    let heightValue = isNaN(heightInput.valueAsNumber)
      ? height
      : heightInput.valueAsNumber;
    let depthValue = isNaN(depthInput.valueAsNumber)
      ? depth
      : depthInput.valueAsNumber;

    setWidth(widthValue);
    setDepth(depthValue);
    setHeight(heightValue);
    widthInput.value = widthValue.toString();
    depthInput.value = depthValue.toString();
    heightInput.value = heightValue.toString();
    const dimensionsObject = {
      width: {
        cm: widthValue,
        in: decimalInchesToNearest16thFraction(cmToIn(widthValue)),
      },
      height: {
        cm: heightValue,
        in: decimalInchesToNearest16thFraction(cmToIn(heightValue)),
      },
      depth: {
        cm: depthValue,
        in: decimalInchesToNearest16thFraction(cmToIn(depthValue)),
      },
    };
    handleSetCurrentInsertDimensions(dimensionsObject);
    setUnitSwitchDisabled(false);
    handleSwitchUnit(true);
    setInputsDisabled(false);
    setInitialLoad(false);
  }, []);

  useEffect(() => {
    let widthInput = document.getElementById("outside_width");
    let depthInput = document.getElementById("outside_depth");
    let heightInput = document.getElementById("outside_height");
    let widthValue = isNaN(widthInput.valueAsNumber)
      ? width
      : widthInput.valueAsNumber;
    let heightValue = isNaN(heightInput.valueAsNumber)
      ? height
      : heightInput.valueAsNumber;
    let depthValue = isNaN(depthInput.valueAsNumber)
      ? depth
      : depthInput.valueAsNumber;

    setWidth(widthValue);
    setDepth(depthValue);
    setHeight(heightValue);
    widthInput.value = widthValue.toString();
    depthInput.value = depthValue.toString();
    heightInput.value = heightValue.toString();
    const dimensionsObject = {
      width: {
        cm: widthValue,
        in: decimalInchesToNearest16thFraction(cmToIn(widthValue)),
      },
      height: {
        cm: heightValue,
        in: decimalInchesToNearest16thFraction(cmToIn(heightValue)),
      },
      depth: {
        cm: depthValue,
        in: decimalInchesToNearest16thFraction(cmToIn(depthValue)),
      },
    };
    handleSetCurrentInsertDimensions(dimensionsObject);
    setUnitSwitchDisabled(false);
  }, [dimensionsUpdate]);

  useEffect(() => {
    if (imperial && currentInsertDimensions) {
      dispatch({
        type: IMPERIAL_ACTIONS.SET_WIDTH_INTEGER,
        value: currentInsertDimensions.width.in.integer,
      });
      dispatch({
        type: IMPERIAL_ACTIONS.SET_WIDTH_FRACTION,
        fraction: currentInsertDimensions.width.in.fraction,
      });
      dispatch({
        type: IMPERIAL_ACTIONS.SET_WIDTH_CM,
        value: currentInsertDimensions.width.cm,
      });
      dispatch({
        type: IMPERIAL_ACTIONS.SET_DEPTH_INTEGER,
        value: currentInsertDimensions.depth.in.integer,
      });
      dispatch({
        type: IMPERIAL_ACTIONS.SET_DEPTH_FRACTION,
        fraction: currentInsertDimensions.depth.in.fraction,
      });
      dispatch({
        type: IMPERIAL_ACTIONS.SET_DEPTH_CM,
        value: currentInsertDimensions.depth.cm,
      });
      dispatch({
        type: IMPERIAL_ACTIONS.SET_HEIGHT_INTEGER,
        value: currentInsertDimensions.height.in.integer,
      });
      dispatch({
        type: IMPERIAL_ACTIONS.SET_HEIGHT_FRACTION,
        fraction: currentInsertDimensions.height.in.fraction,
      });
      dispatch({
        type: IMPERIAL_ACTIONS.SET_HEIGHT_CM,
        value: currentInsertDimensions.height.cm,
      });
    }
  }, [currentInsertDimensions]);

  useEffect(() => {
    let widthInput = document.getElementById("outside_width");
    let depthInput = document.getElementById("outside_depth");
    let heightInput = document.getElementById("outside_height");
    let widthValue = isNaN(widthInput.valueAsNumber)
      ? width
      : widthInput.valueAsNumber;
    let heightValue = isNaN(heightInput.valueAsNumber)
      ? height
      : heightInput.valueAsNumber;
    let depthValue = isNaN(depthInput.valueAsNumber)
      ? depth
      : depthInput.valueAsNumber;

    const dimensionsObject = {
      width: {
        cm: widthValue,
        in: decimalInchesToNearest16thFraction(cmToIn(widthValue)),
      },
      height: {
        cm: heightValue,
        in: decimalInchesToNearest16thFraction(cmToIn(heightValue)),
      },
      depth: {
        cm: depthValue,
        in: decimalInchesToNearest16thFraction(cmToIn(depthValue)),
      },
    };
    handleSetCurrentInsertDimensions(dimensionsObject);
  }, [width, depth, height]);

  useEffect(() => {
    if (!initialLoad) {
      if (dimensionsState.widthCm > 0) {
        if (
          dimensionsState.widthCm >= MIN_WIDTH_DEPTH &&
          dimensionsState.widthCm <= MAX_WIDTH_DEPTH
        ) {
          handleSetWidthError(false);
          setWidth(dimensionsState.widthCm);
          document.getElementById("outside_width").value =
            dimensionsState.widthCm;
          window?.changeWidth?.();
        } else {
          handleSetWidthError(true);
          if (dimensionsState.widthCm < MIN_WIDTH_DEPTH)
            setDimensionsErrorMessage(
              "This is the minimum size we can make an insert using 3/8″ material.  For smaller inserts please contact us."
            );
          else
            setDimensionsErrorMessage(
              "The dimensions are not allowed for this material."
            );
        }
      }
    }
    triggerPriceUpdate?.();
  }, [initialLoad, dimensionsState.widthCm]);

  useEffect(() => {
    if (!initialLoad) {
      if (dimensionsState.depthCm > 0) {
        if (
          dimensionsState.depthCm >= MIN_WIDTH_DEPTH &&
          dimensionsState.depthCm <= MAX_WIDTH_DEPTH
        ) {
          handleSetDepthError(false);
          setDepth(dimensionsState.depthCm);
          document.getElementById("outside_depth").value =
            dimensionsState.depthCm;
          window?.changeDepth?.();
        } else {
          handleSetDepthError(true);
          if (dimensionsState.depthCm < MIN_WIDTH_DEPTH)
            setDimensionsErrorMessage(
              "This is the minimum size we can make an insert using 3/8″ material.  For smaller inserts please contact us."
            );
          else
            setDimensionsErrorMessage(
              "The dimensions are not allowed for this material." +
              dimensionsState.depthCm,
              MIN_WIDTH_DEPTH,
              MAX_WIDTH_DEPTH
            );
        }
      }
    }
    triggerPriceUpdate?.();
  }, [initialLoad, dimensionsState.depthCm]);

  useEffect(() => {
    if (!initialLoad) {
      if (dimensionsState.heightCm > 0) {
        if (dimensionsState.heightCm >= MIN_HEIGHT) {
          switch (selectedMaterial?.name?.toLowerCase()) {
            case "bamboo":
              if (dimensionsState.heightCm <= MAX_HEIGHT_BAMBOO) {
                handleSetHeightError(false);
                setHeight(dimensionsState.heightCm);
                document.getElementById("outside_height").value =
                  dimensionsState.heightCm;
                window?.changeHeight?.();
              } else {
                handleSetHeightError(true);
                setDimensionsErrorMessage(
                  "The dimensions are not allowed for this material."
                );
              }
              break;
            default:
              if (dimensionsState.heightCm <= MAX_HEIGHT_DEFAULT) {
                handleSetHeightError(false);
                setHeight(dimensionsState.heightCm);
                document.getElementById("outside_height").value =
                  dimensionsState.heightCm;
                window?.changeHeight?.();
              } else {
                handleSetHeightError(true);
                setDimensionsErrorMessage(
                  "The dimensions are not allowed for this material."
                );
              }
          }
        } else {
          handleSetHeightError(true);
          setDimensionsErrorMessage(
            "The dimensions are not allowed for this material."
          );
        }
      }
    }
    triggerPriceUpdate?.();
  }, [initialLoad, dimensionsState.heightCm]);

  useEffect(() => {
    const handleDOutsideClick = (event) => {
      if (
        depthFractionDropdownRef.current &&
        !depthFractionDropdownRef.current.contains(event.target)
      ) {
        setDepthFractionsDropdownActive(false);
      }
    };
    document.addEventListener("mousedown", handleDOutsideClick);

    return () => {
      document.removeEventListener("mousedown", handleDOutsideClick);
    };
  }, [depthFractionDropdownRef.current]);

  useEffect(() => {
    const handleWOutsideClick = (event) => {
      if (
        widthFractionDropdownRef.current &&
        !widthFractionDropdownRef.current.contains(event.target)
      ) {
        setWidthFractionsDropdownActive(false);
      }
    };
    document.addEventListener("mousedown", handleWOutsideClick);

    return () => {
      document.removeEventListener("mousedown", handleWOutsideClick);
    };
  }, [widthFractionDropdownRef.current]);

  useEffect(() => {
    const handleHOutsideClick = (event) => {
      if (
        heightFractionDropdownRef.current &&
        !heightFractionDropdownRef.current.contains(event.target)
      ) {
        setHeightFractionsDropdownActive(false);
      }
    };
    document.addEventListener("mousedown", handleHOutsideClick);

    return () => {
      document.removeEventListener("mousedown", handleHOutsideClick);
    };
  }, [heightFractionDropdownRef.current]);

  const handleChangeWidth = (unit, value) => {
    switch (unit) {
      case "imperial": {
        dispatch({
          type: IMPERIAL_ACTIONS.SET_WIDTH_CM,
          value: parseFloat(
            inchFractionsToCmDecimal({
              integer: value.integer,
              fraction: value.fraction,
            })
          ),
        });
        break;
      }
      default: {
        if (value >= MIN_WIDTH_DEPTH && value <= MAX_WIDTH_DEPTH) {
          handleSetWidthError(false);
          setWidth(parseFloat(value));
          window?.changeWidth?.();
        } else {
          handleSetWidthError(true);
          if (dimensionsState.widthCm < MIN_WIDTH_DEPTH)
            setDimensionsErrorMessage(
              "This is the minimum size we can make an insert using 3/8″ material.  For smaller inserts please contact us."
            );
          else
            setDimensionsErrorMessage(
              "The dimensions are not allowed for this material."
            );
        }
      }
    }
  };

  const handleChangeDepth = (unit, value) => {
    switch (unit) {
      case "imperial": {
        dispatch({
          type: IMPERIAL_ACTIONS.SET_DEPTH_CM,
          value: parseFloat(
            inchFractionsToCmDecimal({
              integer: value.integer,
              fraction: value.fraction,
            })
          ),
        });
        break;
      }
      default: {
        if (value >= MIN_WIDTH_DEPTH && value <= MAX_WIDTH_DEPTH) {
          handleSetDepthError(false);
          setDepth(parseFloat(value));
          window?.changeDepth?.();
        } else {
          handleSetDepthError(true);
          if (dimensionsState.depthCm < MIN_WIDTH_DEPTH)
            setDimensionsErrorMessage(
              "This is the minimum size we can make an insert using 3/8″ material.  For smaller inserts please contact us."
            );
          else
            setDimensionsErrorMessage(
              "The dimensions are not allowed for this material." +
              dimensionsState.depthCm,
              MIN_WIDTH_DEPTH,
              MAX_WIDTH_DEPTH
            );
        }
      }
    }
  };

  const handleChangeHeight = (unit, value) => {
    switch (unit) {
      case "imperial": {
        dispatch({
          type: IMPERIAL_ACTIONS.SET_HEIGHT_CM,
          value: parseFloat(
            inchFractionsToCmDecimal({
              integer: value.integer,
              fraction: value.fraction,
            })
          ),
        });
        break;
      }
      default: {
        if (value) {
          switch (selectedMaterial.name.toLowerCase()) {
            case "bamboo":
              if (value <= MAX_HEIGHT_BAMBOO) {
                handleSetHeightError(false);
                setHeight(parseFloat(value));
                window?.changeHeight?.();
              } else {
                handleSetHeightError(true);
                setDimensionsErrorMessage(
                  "The dimensions are not allowed for this material."
                );
              }
              break;
            default:
              if (value <= MAX_HEIGHT_DEFAULT) {
                handleSetHeightError(false);
                setHeight(parseFloat(value));
                window?.changeHeight?.();
              } else {
                handleSetHeightError(true);
                setDimensionsErrorMessage(
                  "The dimensions are not allowed for this material."
                );
              }
          }
        } else {
          handleSetHeightError(true);
          setDimensionsErrorMessage(
            "The dimensions are not allowed for this material."
          );
        }
      }
    }
  };

  const handleSwitchUnit = (checked) => {
    if (checked) {
      const widthInchFractions = decimalInchesToNearest16thFraction(
        cmToIn(width)
      );
      dispatch({
        type: IMPERIAL_ACTIONS.SET_WIDTH_INTEGER,
        value: widthInchFractions.integer,
      });
      dispatch({
        type: IMPERIAL_ACTIONS.SET_WIDTH_FRACTION,
        fraction: widthInchFractions.fraction,
      });
      dispatch({
        type: IMPERIAL_ACTIONS.SET_WIDTH_CM,
        value: width,
      });
      const depthInchFractions = decimalInchesToNearest16thFraction(
        cmToIn(depth)
      );
      dispatch({
        type: IMPERIAL_ACTIONS.SET_DEPTH_INTEGER,
        value: depthInchFractions.integer,
      });
      dispatch({
        type: IMPERIAL_ACTIONS.SET_DEPTH_FRACTION,
        fraction: depthInchFractions.fraction,
      });
      dispatch({
        type: IMPERIAL_ACTIONS.SET_DEPTH_CM,
        value: depth,
      });
      const heightInchFractions = decimalInchesToNearest16thFraction(
        cmToIn(height)
      );
      dispatch({
        type: IMPERIAL_ACTIONS.SET_HEIGHT_INTEGER,
        value: heightInchFractions.integer,
      });
      dispatch({
        type: IMPERIAL_ACTIONS.SET_HEIGHT_FRACTION,
        fraction: heightInchFractions.fraction,
      });
      dispatch({
        type: IMPERIAL_ACTIONS.SET_HEIGHT_CM,
        value: height,
      });
      handleSetImperial(true);
    } else handleSetImperial(false);
    window?.impSwitch?.();
    triggerDimensionsUpdate();
  };

  const labelStyle = {
    color: readableColor(variables.menuBgColor),
  };

  return (
    <>
      {(widthError || depthError || heightError) && (
        <div className="dimension-error-tooltip">{dimensionsErrorMessage}</div>
      )}
      <div className="dimensions" id="dimensions">
        {showTooltips && (
          <ReactTooltip
            id="dimensions-tip"
            place="left"
            effect="solid"
            className="tooltip"
          >
            <p style={{ maxWidth: "200px" }}>
              Measurement should be 1/8"(3mm) smaller than the inside dimension
              of your drawer box
            </p>
          </ReactTooltip>
        )}
        <p className="dimensions--title title is-size-6" style={labelStyle}>
          Insert Dimensions
        </p>
        <div className="is-flex is-align-items-center is-justify-content-space-between mb-2">
          <div
            className="dimensions--text-input"
            data-tip
            data-for="width-error-tip"
          >
            <label style={labelStyle}>
              Width
              {imperial && (
                <div className="is-flex is-align-items-center">
                  <input
                    className={`dimensions--inch-input ${widthError ? "input-error" : ""}`}
                    id="outside_width_inch_full"
                    type="number"
                    disabled={inputsDisabled}
                    value={dimensionsState.width_integer}
                    data-tip
                    data-for="dimensions-tip"
                    onChange={(e) => {
                      dispatch({
                        type: IMPERIAL_ACTIONS.SET_WIDTH_INTEGER,
                        value: parseInt(e.target.value),
                      });
                      handleChangeWidth("imperial", {
                        fraction: dimensionsState.width_fraction,
                        integer: parseInt(e.target.value),
                      });
                    }}
                  />
                  <div
                    className={`dimensions--fraction-dropdown dropdown ${widthFractionsDropdownActive ? "is-active" : ""
                      } ${inputsDisabled ? "is-disabled" : ""}`}
                    ref={widthFractionDropdownRef}
                  >
                    <div className="dropdown-trigger" style={{ overflow: "hidden" }}>
                      <button
                        className={`button is-outlined reset-button is-light ${widthError ? "input-error" : ""
                          }`}
                        id="outside_width_inch_fraction"
                        aria-haspopup="true"
                        aria-controls="width-fractions"
                        data-tip
                        data-for="dimensions-tip"
                        onClick={() => {
                          if (!inputsDisabled)
                            setWidthFractionsDropdownActive(
                              !widthFractionsDropdownActive
                            );
                        }}
                      >
                        {dimensionsState.width_fraction.numerator === 0 ? (
                          <span>0</span>
                        ) : (
                          <span>
                            {dimensionsState.width_fraction.numerator}/
                            {dimensionsState.width_fraction.denominator}
                          </span>
                        )}
                        <span className="icon is-small">
                          <FaCaretDown />
                        </span>
                      </button>
                    </div>
                    <div
                      className="dropdown-menu"
                      id="width-fractions"
                      role="menu"
                    >
                      <div className="dropdown-content has-text-black">
                        {FRACTIONS_V2.map((fraction, index) => (
                          <button
                            className="reset-button dropdown-item"
                            key={"width-fraction-" + index}
                            data-tip
                            data-for="dimensions-tip"
                            onClick={() => {
                              dispatch({
                                type: IMPERIAL_ACTIONS.SET_WIDTH_FRACTION,
                                fraction,
                              });
                              handleChangeWidth("imperial", {
                                fraction,
                                integer: dimensionsState.width_integer,
                              });
                              setWidthFractionsDropdownActive(false);
                            }}
                          >
                            {fraction.numerator === 0
                              ? "0"
                              : `${fraction.numerator}/${fraction.denominator}`}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                  <span className={`unit ${widthError ? "input-error" : ""}`}>
                    in
                  </span>
                </div>
              )}
              <div className={`is-flex ${imperial ? "is-hidden" : ""}`}>
                <input
                  className={`cm-input ${widthError ? "input-error" : ""}`}
                  type="number"
                  step="0.01"
                  min="0"
                  id="outside_width"
                  data-tip
                  data-for="dimensions-tip"
                  onChange={(e) => handleChangeWidth("metric", e.target.value)}
                />
                <span className={`unit ${widthError ? "input-error" : ""}`}>
                  cm
                </span>
              </div>
            </label>
          </div>
          <div
            className="dimensions--text-input"
            data-tip
            data-for="depth-error-tip"
          >
            <label style={labelStyle}>
              Depth
              {imperial && (
                <div className="is-flex is-align-items-center">
                  <input
                    className={`dimensions--inch-input ${depthError ? "input-error" : ""}`}
                    id="outside_depth_inch_full"
                    type="number"
                    data-tip
                    data-for="dimensions-tip"
                    disabled={inputsDisabled}
                    value={dimensionsState.depth_integer}
                    onChange={(e) => {
                      dispatch({
                        type: IMPERIAL_ACTIONS.SET_DEPTH_INTEGER,
                        value: parseInt(e.target.value),
                      });
                      handleChangeDepth("imperial", {
                        fraction: dimensionsState.depth_fraction,
                        integer: parseInt(e.target.value),
                      });
                    }}
                  />
                  <div
                    className={`dimensions--fraction-dropdown dropdown ${depthFractionsDropdownActive ? "is-active" : ""
                      } ${inputsDisabled ? "is-disabled" : ""}`}
                    ref={depthFractionDropdownRef}
                  >
                    <div className="dropdown-trigger" style={{ overflow: "hidden" }}>
                      <button
                        className={`button is-outlined reset-button is-light ${depthError ? "input-error" : ""
                          }`}
                        id="outside_depth_inch_fraction"
                        aria-haspopup="true"
                        data-tip
                        data-for="dimensions-tip"
                        aria-controls="depth-fractions"
                        onClick={() => {
                          if (!inputsDisabled)
                            setDepthFractionsDropdownActive(
                              !depthFractionsDropdownActive
                            );
                        }}
                      >
                        {dimensionsState.depth_fraction.numerator === 0 ? (
                          <span>0</span>
                        ) : (
                          <span>
                            {dimensionsState.depth_fraction.numerator}/
                            {dimensionsState.depth_fraction.denominator}
                          </span>
                        )}
                        <span className="icon is-small">
                          <FaCaretDown></FaCaretDown>
                        </span>
                      </button>
                    </div>
                    <div
                      className="dropdown-menu"
                      id="depth-fractions"
                      role="menu"
                    >
                      <div className="dropdown-content has-text-black">
                        {FRACTIONS_V2.map((fraction, index) => (
                          <button
                            className="reset-button dropdown-item"
                            key={"depth-fraction-" + index}
                            data-tip
                            data-for="dimensions-tip"
                            onClick={() => {
                              dispatch({
                                type: IMPERIAL_ACTIONS.SET_DEPTH_FRACTION,
                                fraction,
                              });
                              handleChangeDepth("imperial", {
                                fraction,
                                integer: dimensionsState.depth_integer,
                              });
                              setDepthFractionsDropdownActive(false);
                            }}
                          >
                            {fraction.numerator === 0
                              ? "0"
                              : `${fraction.numerator}/${fraction.denominator}`}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                  <span className={`unit ${depthError ? "input-error" : ""}`}>
                    in
                  </span>
                </div>
              )}
              <div className={`is-flex ${imperial ? "is-hidden" : ""}`}>
                <input
                  className={`cm-input ${depthError ? "input-error" : ""}`}
                  type="number"
                  step="0.01"
                  data-tip
                  data-for="dimensions-tip"
                  min="0"
                  id="outside_depth"
                  onChange={(e) => handleChangeDepth("metric", e.target.value)}
                />
                <span className={`unit ${depthError ? "input-error" : ""}`}>
                  cm
                </span>
              </div>
            </label>
          </div>
        </div>
        <div className="is-flex is-align-items-flex-end is-justify-content-space-between">
          <div
            className="dimensions--text-input"
            data-tip
            data-for="height-error-tip"
          >
            <label style={labelStyle}>
              Height
              {imperial && (
                <div className="is-flex is-align-items-center">
                  <input
                    className={`dimensions--inch-input ${heightError ? "input-error" : ""}`}
                    id="outside_height_inch_full"
                    type="number"
                    disabled={inputsDisabled}
                    value={dimensionsState.height_integer}
                    onChange={(e) => {
                      dispatch({
                        type: IMPERIAL_ACTIONS.SET_HEIGHT_INTEGER,
                        value: parseInt(e.target.value),
                      });
                      handleChangeHeight("imperial", {
                        fraction: dimensionsState.height_fraction,
                        integer: parseInt(e.target.value),
                      });
                    }}
                  />
                  <div
                    className={`dimensions--fraction-dropdown dropdown ${heightFractionsDropdownActive ? "is-active" : ""
                      } ${inputsDisabled ? "is-disabled" : ""}`}
                    ref={heightFractionDropdownRef}
                  >
                    <div className="dropdown-trigger" style={{ overflow: "hidden" }}>
                      <button
                        className={`button is-outlined reset-button is-light ${heightError ? "input-error" : ""
                          }`}
                        id="outside_height_inch_fraction"
                        aria-haspopup="true"
                        aria-controls="height-fractions"
                        onClick={() => {
                          if (!inputsDisabled)
                            setHeightFractionsDropdownActive(
                              !heightFractionsDropdownActive
                            );
                        }}
                      >
                        {dimensionsState.height_fraction.numerator === 0 ? (
                          <span>0</span>
                        ) : (
                          <span>
                            {dimensionsState.height_fraction.numerator}/
                            {dimensionsState.height_fraction.denominator}
                          </span>
                        )}
                        <span className="icon is-small">
                          <FaCaretDown></FaCaretDown>
                        </span>
                      </button>
                    </div>
                    <div
                      className="dropdown-menu"
                      id="height-fractions"
                      role="menu"
                    >
                      <div className="dropdown-content has-text-black">
                        {FRACTIONS_V2.map((fraction, index) => (
                          <button
                            className="reset-button dropdown-item"
                            key={"height-fraction-" + index}
                            data-tip
                            data-for="dimensions-tip"
                            onClick={() => {
                              dispatch({
                                type: IMPERIAL_ACTIONS.SET_HEIGHT_FRACTION,
                                fraction,
                              });
                              handleChangeHeight("imperial", {
                                fraction,
                                integer: dimensionsState.height_integer,
                              });
                              setHeightFractionsDropdownActive(false);
                            }}
                          >
                            {fraction.numerator === 0
                              ? "0"
                              : `${fraction.numerator}/${fraction.denominator}`}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                  <span className={`unit ${heightError ? "input-error" : ""}`}>
                    in
                  </span>
                </div>
              )}
              <div className={`is-flex ${imperial ? "is-hidden" : ""}`}>
                <input
                  className={`cm-input ${heightError ? "input-error" : ""}`}
                  type="number"
                  step="0.01"
                  min="0"
                  id="outside_height"
                  onChange={(e) => handleChangeHeight("metric", e.target.value)}
                />
                <span className={`unit ${heightError ? "input-error" : ""}`}>
                  cm
                </span>
              </div>
            </label>
          </div>
          <div className="dimensions--unit">
            <span
              style={{
                color: readableColor(variables.menuBgColor),
                paddingBottom: "0.5rem",
              }}
            >
              Unit
            </span>
            <label className="unit-switch">
              <input
                type="checkbox"
                id="flexSwitchCheckImperial"
                onClick={(e) => handleSwitchUnit(e.target.checked)}
                value={imperial}
                data-tip
                data-for="dimensions-tip"
                defaultChecked={true}
                disabled={unitSwitchDisabled}
              />
              <span className="imperial" style={labelStyle}>
                Imperial
              </span>
              <span className="metric" style={labelStyle}>
                Metric
              </span>
            </label>
          </div>
        </div>
      </div>
      <hr className="horizontal"></hr>
    </>
  );
};

export default Dimensions;
